import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdAdd, MdCheck, MdClose, MdDragIndicator, MdDelete, MdCircle } from 'react-icons/md';
import { useTenant } from '@contexts/TenantContext';
import { cohortStart } from '@services/cloudFunctions';
import notificationService from '@services/notificationService';
import latteMascot from '@assets/img/latte/3.png';

interface PresetCardProps {
  id: string;
  name: string;
  description: string;
  actionCount: number;
  isSelected: boolean;
  recommended?: boolean;
  comingSoon?: boolean;
  mascotImage?: string;
  mascotSize?: 'sm' | 'lg';
  onClick: () => void;
}

const PresetCard = ({
  id,
  name,
  description,
  actionCount,
  isSelected,
  recommended,
  comingSoon,
  mascotImage,
  mascotSize = 'sm',
  onClick,
}: PresetCardProps) => {
  return (
    <button
      onClick={onClick}
      className={`relative text-left p-4 rounded-xl border-2 transition-all ${
        isSelected
          ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
          : 'border-gray-200 dark:border-gray-700 hover:border-brand-300 dark:hover:border-brand-700 hover:bg-gray-50 dark:hover:bg-navy-900'
      }`}
    >
      {mascotImage && (
        <div className={`absolute right-4 ${mascotSize === 'lg' ? '-top-16' : '-top-11'}`}>
          <img
            src={mascotImage}
            alt={recommended ? "Recommended" : "Mascot"}
            className={`object-contain ${mascotSize === 'lg' ? 'h-20 w-20' : 'h-14 w-14'}`}
          />
        </div>
      )}
      <div className="flex items-center gap-2 mb-2">
        <h4 className={`text-sm font-bold ${
          isSelected
            ? 'text-brand-600 dark:text-brand-400'
            : 'text-navy-700 dark:text-white'
        }`}>
          {name}
        </h4>
        {comingSoon && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 font-medium">
            Coming Soon
          </span>
        )}
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
          isSelected
            ? 'bg-brand-200 dark:bg-brand-800 text-brand-700 dark:text-brand-300'
            : 'bg-gray-100 dark:bg-navy-700 text-gray-600 dark:text-gray-400'
        }`}>
          {actionCount} {actionCount === 1 ? 'email' : 'emails'}
        </span>
      </div>
      <p className="text-xs text-gray-600 dark:text-gray-400">
        {description}
      </p>
      {isSelected && (
        <div className="absolute top-3 right-3">
          <MdCheck className="h-5 w-5 text-brand-500" />
        </div>
      )}
    </button>
  );
};

interface WorkflowAction {
  id: number;
  title: string;
  description: string;
  enabled: boolean;
  daysAfterPrevious: number;
  isFirst?: boolean;
}

interface WorkflowPreset {
  id: string;
  name: string;
  description: string;
  actionIds: number[];
  recommended?: boolean;
  fixedDays?: { [actionId: number]: number };
  locked?: boolean;
}

const IntroductionTemplates = () => {
  const navigate = useNavigate();
  const { currentTenantId } = useTenant();

  const [openStepPopup, setOpenStepPopup] = useState<number | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<string>('standard');
  const [cohortName, setCohortName] = useState<string>('');
  const [isCreatingCohort, setIsCreatingCohort] = useState(false);
  const [showCreateCohortModal, setShowCreateCohortModal] = useState(false);

  // Preset workflows
  const workflowPresets: WorkflowPreset[] = [
    {
      id: 'standard',
      name: 'Standard',
      description: 'Full control - trigger each email action yourself when ready',
      actionIds: [0, 12, 5, 1, 3, 4],
      fixedDays: { 0: 0, 12: 0, 5: 0, 1: 0, 3: 0, 4: 0 },
    },
    {
      id: 'automated-double-opt-in',
      name: 'Double Opt-In',
      description: 'Requires both participants to opt-in before introduction email is sent',
      actionIds: [0, 12, 5, 11, 3, 4],
      fixedDays: { 0: 0, 12: 0, 5: 0, 11: 0, 3: 0, 4: 0 },
    },
    {
      id: 'automated',
      name: 'Automated',
      description: 'Latte AI sends Welcome (Day 1) → Connection (Day 8) → Feedback (Day 22) automatically',
      actionIds: [0, 12, 5, 1, 4],
      recommended: true,
      fixedDays: { 0: 0, 12: 0, 5: 1, 1: 7, 4: 14 },
      locked: true,
    },
    {
      id: 'custom',
      name: 'Custom',
      description: 'Build your own workflow with full customization options',
      actionIds: [0, 12, 5, 6, 1, 9, 2, 3, 7, 4, 8, 10],
      fixedDays: { 0: 0, 12: 0, 5: 1, 6: 3, 1: 1, 9: 3, 2: 7, 3: 7, 7: 7, 4: 7, 8: 14, 10: 7 },
    },
  ];

  // All available preset actions
  const availablePresets: WorkflowAction[] = [
    {
      id: 0,
      title: 'Select Participants',
      description: 'Choose which users will participate in this introduction cohort',
      enabled: true,
      daysAfterPrevious: 0,
      isFirst: true
    },
    {
      id: 1,
      title: 'Connection Email',
      description: 'Initial introduction email sent to matched pairs',
      enabled: true,
      daysAfterPrevious: 0,
    },
    {
      id: 2,
      title: 'Follow-up Email',
      description: 'Reminder email for pairs who haven\'t connected',
      enabled: true,
      daysAfterPrevious: 7
    },
    {
      id: 3,
      title: 'Check-in Email',
      description: 'Mid-program check-in to see how connections are going',
      enabled: true,
      daysAfterPrevious: 14
    },
    {
      id: 4,
      title: 'Feedback Request',
      description: 'Survey email to collect participant feedback',
      enabled: true,
      daysAfterPrevious: 30
    },
    {
      id: 5,
      title: 'Welcome Package',
      description: 'Send onboarding materials and program guidelines',
      enabled: false,
      daysAfterPrevious: 1
    },
    {
      id: 6,
      title: 'Success Tips',
      description: 'Best practices for building meaningful connections',
      enabled: false,
      daysAfterPrevious: 3
    },
    {
      id: 7,
      title: 'Mid-Program Survey',
      description: 'Quick check-in survey to gauge satisfaction',
      enabled: false,
      daysAfterPrevious: 21
    },
    {
      id: 8,
      title: 'Celebration Email',
      description: 'Congratulations for completing the program',
      enabled: false,
      daysAfterPrevious: 60
    },
    {
      id: 9,
      title: 'Resources Email',
      description: 'Share helpful resources and conversation starters',
      enabled: false,
      daysAfterPrevious: 5
    },
    {
      id: 10,
      title: 'Final Thank You',
      description: 'Thank participants and invite them to future programs',
      enabled: false,
      daysAfterPrevious: 90
    },
    {
      id: 11,
      title: 'Introduce and Connect',
      description: 'Sends intro emails to potential matches. Once both sides opt in, an official connection email is sent.',
      enabled: false,
      daysAfterPrevious: 0
    },
    {
      id: 12,
      title: 'Create Matches',
      description: 'Generate AI-powered matches between mentors and mentees',
      enabled: true,
      daysAfterPrevious: 0
    }
  ];

  // Define workflow actions - initialize with Standard preset (all enabled, correct order)
  const [actions, setActions] = useState<WorkflowAction[]>(
    [0, 12, 5, 1, 3, 4]
      .map(id => availablePresets.find(preset => preset.id === id)!)
      .map(action => ({ ...action, enabled: true }))
  );

  // Apply a preset workflow
  const applyPreset = (presetId: string) => {
    const preset = workflowPresets.find(p => p.id === presetId);
    if (preset) {
      const newActions = preset.actionIds.map(actionId => {
        const action = availablePresets.find(a => a.id === actionId);
        if (!action) return null;
        const daysAfterPrevious = preset.fixedDays?.[actionId] ?? action.daysAfterPrevious;
        return { ...action, enabled: true, daysAfterPrevious };
      }).filter(Boolean) as WorkflowAction[];
      setActions(newActions);
      setSelectedPreset(presetId);
    }
  };

  // Check if the current preset has locked timing
  const isTimingLocked = () => {
    const preset = workflowPresets.find(p => p.id === selectedPreset);
    return preset?.locked ?? false;
  };

  const handleToggleAction = (id: number) => {
    setActions(actions.map(action =>
      action.id === id && !action.isFirst ? { ...action, enabled: !action.enabled } : action
    ));
  };

  const handleDaysChange = (id: number, days: number) => {
    // Enforce min 0 and max 30 days
    const clampedDays = Math.min(Math.max(0, days), 30);
    setActions(actions.map(action =>
      action.id === id ? { ...action, daysAfterPrevious: clampedDays } : action
    ));
  };

  const enabledActions = actions.filter(a => a.enabled);
  const totalActions = actions.length;

  // Get available presets that aren't already in the workflow
  const getAvailablePresets = () => {
    const actionIds = actions.map(a => a.id);
    return availablePresets.filter(preset => !actionIds.includes(preset.id));
  };

  const handleAddPreset = (presetId: number) => {
    const preset = availablePresets.find(p => p.id === presetId);
    if (preset) {
      setActions([...actions, { ...preset, enabled: true }]);
      setOpenStepPopup(null);
    }
  };

  const handleRemoveAction = (id: number) => {
    setActions(actions.filter(action => action.id !== id));
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedIndex === null || draggedIndex === index) return;

    setDragOverIndex(index);
  };

  const handleDragLeave = (e: React.DragEvent, index: number) => {
    // Only clear if we're actually leaving this item's area
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (
      x < rect.left ||
      x > rect.right ||
      y < rect.top ||
      y > rect.bottom
    ) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newActions = [...actions];
    const [draggedAction] = newActions.splice(draggedIndex, 1);

    // If dropping at the end (past the last index), just append
    if (dropIndex >= actions.length) {
      newActions.push(draggedAction);
    } else {
      // Adjust drop index if dragging from before the drop position
      const adjustedDropIndex = draggedIndex < dropIndex ? dropIndex - 1 : dropIndex;
      newActions.splice(adjustedDropIndex, 0, draggedAction);
    }

    setActions(newActions);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Calculate cumulative days for timeline
  const getCumulativeDays = (index: number) => {
    let total = 0;
    for (let i = 0; i <= index; i++) {
      if (actions[i].enabled) {
        total += actions[i].daysAfterPrevious;
      }
    }
    return total;
  };

  // Map action IDs to semantic keys
  const actionIdToKey: { [id: number]: string } = {
    0: 'selectParticipants',
    1: 'connectionEmail',
    2: 'followUpEmail',
    3: 'checkInEmail',
    4: 'feedbackRequest',
    5: 'welcomePackage',
    6: 'successTips',
    7: 'midProgramSurvey',
    8: 'celebrationEmail',
    9: 'resourcesEmail',
    10: 'finalThankYou',
    11: 'introduceAndConnect',
    12: 'createMatches',
  };

  // Build preset actions array from current workflow
  const buildPresetActions = () => {
    return actions.map((action) => ({
      key: actionIdToKey[action.id] || `action_${action.id}`,
      title: action.title,
      description: action.description,
      daysAfterPrevious: action.daysAfterPrevious,
      enabled: action.enabled,
    }));
  };

  // Create cohort with preset actions
  const handleCreateCohort = async () => {
    if (!currentTenantId) {
      notificationService.error('No tenant selected');
      return;
    }

    if (!cohortName.trim()) {
      notificationService.error('Please enter a cohort name');
      return;
    }

    setIsCreatingCohort(true);
    try {
      const presetActions = buildPresetActions();

      const result = await cohortStart({
        tenant_id: currentTenantId,
        title: cohortName.trim(),
        status: 'draft',
        presetActions,
        workflowType: selectedPreset,
      });

      if (result.success) {
        notificationService.success('Cohort created successfully!');
        setShowCreateCohortModal(false);
        setCohortName('');

        // Navigate to AI Introductions page with the new cohort selected
        const newCohortId = result.data?.cohort_id;
        if (newCohortId) {
          localStorage.setItem('selectedCohortId', newCohortId);
        }
        navigate('/admin/intros/engine');
      } else {
        notificationService.error(`Failed to create cohort: ${result.error}`);
      }
    } catch (error) {
      console.error('Error creating cohort:', error);
      notificationService.error('Failed to create cohort');
    } finally {
      setIsCreatingCohort(false);
    }
  };

  return (
    <div className="mt-8 w-full min-h-0 space-y-6">
      {/* Preset Workflows Section */}
      <div className="px-4 py-4 bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-bold text-navy-700 dark:text-white">
              Workflow Presets
            </h3>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Choose a preset to quickly configure your email workflow
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {workflowPresets.map((preset) => (
            <PresetCard
              key={preset.id}
              id={preset.id}
              name={preset.name}
              description={preset.description}
              actionCount={preset.actionIds.filter(id => id !== 0 && id !== 12).length}
              isSelected={selectedPreset === preset.id}
              recommended={preset.recommended}
              comingSoon={preset.id === 'automated' || preset.id === 'custom'}
              mascotImage={preset.id === 'automated-double-opt-in' ? latteMascot : undefined}
              onClick={() => applyPreset(preset.id)}
            />
          ))}
        </div>
      </div>

      {/* Workflow Configuration Container */}
      <div className="px-4 py-4 bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
        {/* Progress Summary */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                Email Workflow Configuration
              </h3>
            </div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              {enabledActions.length}/{totalActions} ACTIONS ENABLED
            </p>
          </div>
          {selectedPreset === 'custom' && (
            <button
              onClick={() => setOpenStepPopup(999)}
              className="flex items-center gap-2 rounded-xl bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-300"
            >
              <MdAdd className="h-4 w-4" />
              Add Action
            </button>
          )}
        </div>

        {/* Actions List */}
        <div className="space-y-3">
          {actions.map((action, index) => (
            <div key={action.id} className="relative">
              {/* Drop indicator above - shows where item will be inserted */}
              {dragOverIndex === index && draggedIndex !== null && draggedIndex !== index && (
                <div className="absolute -top-2 left-0 right-0 h-1 bg-brand-500 rounded-full z-10 shadow-lg pointer-events-none">
                  <div className="absolute inset-0 bg-brand-500 animate-pulse rounded-full"></div>
                </div>
              )}

              <div
                draggable={action.id !== 0 && selectedPreset === 'custom'}
                onDragStart={(e) => action.id !== 0 && selectedPreset === 'custom' && handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={(e) => handleDragLeave(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                className={`border rounded-xl p-4 transition-all duration-200 ${
                  action.enabled
                    ? 'border-brand-200 dark:border-brand-800 bg-brand-50/50 dark:bg-brand-900/10'
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-navy-900 opacity-60'
                } ${
                  draggedIndex === index ? 'opacity-40 scale-[0.98] shadow-lg' : ''
                } ${
                  dragOverIndex === index && draggedIndex !== index ? 'scale-[1.02] ring-2 ring-brand-500/50' : ''
                }`}
              >
                <div className="flex items-center gap-4">
                  {/* Drag Handle */}
                  {selectedPreset === 'custom' && (
                    <div
                      className={`${action.id !== 0 ? 'text-gray-400 transition-all cursor-grab active:cursor-grabbing hover:text-brand-500 hover:scale-110' : 'invisible'}`}
                    >
                      <MdDragIndicator className="h-5 w-5" />
                    </div>
                  )}

                {/* Step Number */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-sm ${
                  action.enabled
                    ? 'bg-brand-500 text-white'
                    : 'border-2 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400'
                }`}>
                  {action.enabled ? (
                    <MdCheck className="h-4 w-4" />
                  ) : (
                    action.id
                  )}
                </div>

                {/* Action Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className={`text-sm font-bold ${
                      action.enabled
                        ? 'text-navy-700 dark:text-white'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {action.title}
                    </h4>
                    {action.isFirst && (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium">
                        REQUIRED
                      </span>
                    )}
                  </div>
                  <p className={`text-xs ${
                    action.enabled
                      ? 'text-gray-600 dark:text-gray-400'
                      : 'text-gray-400 dark:text-gray-500'
                  }`}>
                    {action.description}
                  </p>
                </div>

                {/* Days Input - only show for automated and custom presets */}
                {!action.isFirst && selectedPreset !== 'standard' && selectedPreset !== 'automated-double-opt-in' && (
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      Days after previous:
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="30"
                      value={action.daysAfterPrevious}
                      onChange={(e) => handleDaysChange(action.id, parseInt(e.target.value) || 0)}
                      disabled={!action.enabled || isTimingLocked()}
                      className={`w-20 px-3 py-1.5 rounded-lg border text-sm text-center font-semibold transition ${
                        action.enabled && !isTimingLocked()
                          ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-navy-900 text-navy-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500'
                          : 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-navy-800 text-gray-400 cursor-not-allowed'
                      }`}
                    />
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      days <span className="text-[10px]">(0-30)</span>
                    </span>
                  </div>
                )}

                {/* Toggle Switch */}
                {action.id !== 0 && (
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={action.enabled}
                      onChange={() => handleToggleAction(action.id)}
                      disabled={action.isFirst || isTimingLocked() || selectedPreset === 'standard' || selectedPreset === 'automated-double-opt-in'}
                      className="sr-only peer"
                    />
                    <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-brand-300 dark:peer-focus:ring-brand-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-brand-500 ${
                      action.isFirst || isTimingLocked() || selectedPreset === 'standard' || selectedPreset === 'automated-double-opt-in' ? 'opacity-50 cursor-not-allowed' : ''
                    }`}></div>
                  </label>
                )}

                {/* Edit Button - only show for automated and custom presets */}
                {action.id !== 0 && selectedPreset !== 'standard' && selectedPreset !== 'automated-double-opt-in' && (
                  <button
                    onClick={() => setOpenStepPopup(action.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                      action.enabled
                        ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-600 hover:bg-brand-100 dark:hover:bg-brand-900/40 hover:text-brand-700 dark:hover:text-brand-400'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                    }`}
                    disabled={!action.enabled}
                  >
                    Customize
                  </button>
                )}

                {/* Remove Button - only show for custom preset */}
                {!action.isFirst && selectedPreset === 'custom' && (
                  <button
                    onClick={() => handleRemoveAction(action.id)}
                    className="px-2 py-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                    title="Remove from workflow"
                  >
                    <MdDelete className="h-4 w-4" />
                  </button>
                )}
              </div>

                {/* Timeline indicator */}
                {action.enabled && index < actions.length - 1 && (
                  <div className="ml-14 mt-3 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <div className="h-8 w-0.5 bg-gradient-to-b from-brand-300 to-transparent dark:from-brand-700"></div>
                    {actions.slice(index + 1).find(a => a.enabled) && (
                      <span className="text-[10px] bg-gray-100 dark:bg-navy-700 px-2 py-0.5 rounded">
                        {selectedPreset === 'standard' || selectedPreset === 'automated-double-opt-in'
                          ? 'Manual trigger'
                          : `+${actions[index + 1].enabled ? actions[index + 1].daysAfterPrevious : 0} days`}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Timeline Summary */}
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-3">
            Workflow Timeline
          </h4>
          <div className="space-y-2">
            {actions.filter(a => a.enabled).map((action, index) => {
              const cumulativeDays = getCumulativeDays(actions.indexOf(action));
              return (
                <div key={action.id} className="flex items-center gap-3 text-xs">
                  <span className={`font-mono font-semibold text-blue-700 dark:text-blue-400 ${selectedPreset === 'standard' || selectedPreset === 'automated-double-opt-in' ? 'w-28' : 'w-12'}`}>
                    {selectedPreset === 'standard' || selectedPreset === 'automated-double-opt-in' ? 'Manual trigger' : `Day ${cumulativeDays}`}
                  </span>
                  <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                  <span className="text-blue-900 dark:text-blue-200">
                    {action.title}
                  </span>
                </div>
              );
            })}
          </div>
          {selectedPreset !== 'standard' && selectedPreset !== 'automated-double-opt-in' && (
            <div className="mt-3 pt-3 border-t border-blue-300 dark:border-blue-700">
              <p className="text-xs text-blue-700 dark:text-blue-400">
                Total workflow duration: <span className="font-bold">{getCumulativeDays(actions.length - 1)} days</span>
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-end gap-3">
          {selectedPreset !== 'standard' && selectedPreset !== 'automated-double-opt-in' && (
            <button
              onClick={() => {
                // TODO: Reset to defaults
              }}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-navy-700 transition text-sm"
            >
              Reset to Defaults
            </button>
          )}
          {(selectedPreset === 'standard' || selectedPreset === 'automated-double-opt-in') ? (
            <button
              onClick={() => {
                setCohortName(`Cohort ${new Date().toLocaleDateString()}`);
                setShowCreateCohortModal(true);
              }}
              className="px-6 py-2 rounded-lg bg-brand-500 text-white font-medium hover:bg-brand-600 transition text-sm"
            >
              Create Cohort
            </button>
          ) : !isTimingLocked() && (
            <button
              onClick={() => {
                // TODO: Save workflow configuration
                alert('Workflow configuration saved!');
              }}
              className="px-6 py-2 rounded-lg bg-brand-500 text-white font-medium hover:bg-brand-600 transition text-sm"
            >
              Save Workflow
            </button>
          )}
        </div>
      </div>

      {/* Customize Action Modal */}
      {openStepPopup && (
        <div
          className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => setOpenStepPopup(null)}
        >
          <div
            className="modal-content bg-white dark:bg-navy-800 rounded-xl shadow-xl max-w-4xl w-full p-6 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                {openStepPopup === 999 ? 'Add Action to Workflow' : `Customize: ${actions.find(a => a.id === openStepPopup)?.title}`}
              </h3>
              <button
                onClick={() => setOpenStepPopup(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <MdClose className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="space-y-4">
              {openStepPopup === 999 ? (
                // Add Preset Action
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Select a preset action to add to your email workflow.
                  </p>

                  {/* Available Presets Grid */}
                  {getAvailablePresets().length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-2">
                      {getAvailablePresets().map((preset) => (
                        <button
                          key={preset.id}
                          onClick={() => handleAddPreset(preset.id)}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-brand-500 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-all text-left group"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="text-sm font-bold text-navy-700 dark:text-white group-hover:text-brand-600 dark:group-hover:text-brand-400">
                              {preset.title}
                            </h4>
                            <MdAdd className="h-5 w-5 text-brand-500 flex-shrink-0" />
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                            {preset.description}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-500">
                            <span className="bg-gray-100 dark:bg-navy-700 px-2 py-1 rounded font-medium">
                              Suggested: +{preset.daysAfterPrevious} days
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        All available preset actions have been added to your workflow.
                      </p>
                    </div>
                  )}

                  <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => setOpenStepPopup(null)}
                      className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-navy-700 transition"
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : (
                // Customize Existing Action
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Add key points and guidelines that AI will use to generate personalized emails for this action.
                  </p>

                  {/* Info Banner */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <MdCheck className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-1">
                          How it works
                        </h4>
                        <p className="text-xs text-blue-700 dark:text-blue-400">
                          The AI will use your key points to craft personalized emails that include participant names, match reasons, and other dynamic content.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Key Points List */}
                  <div>
                    <label className="block text-sm font-medium text-navy-700 dark:text-white mb-3">
                      Key Points to Include
                    </label>
                    <div className="space-y-2">
                      {/* Example key points based on action type */}
                      {(openStepPopup === 0 ? [
                        'Define criteria for participant selection',
                        'Set cohort size and matching preferences',
                        'Review and approve selected participants',
                        'Ensure participant profiles are complete'
                      ] : openStepPopup === 1 ? [
                        'Welcome both participants warmly',
                        'Explain why they were matched together',
                        'Highlight their shared interests and backgrounds',
                        'Encourage them to schedule their first meeting',
                        'Provide conversation starters or icebreaker questions'
                      ] : openStepPopup === 2 ? [
                        'Politely check if they\'ve had a chance to connect',
                        'Remind them of the benefits of the connection',
                        'Suggest taking the first step if they haven\'t already',
                        'Offer to help if they have any questions'
                      ] : openStepPopup === 3 ? [
                        'Ask how their connection is progressing',
                        'Invite them to share any challenges or wins',
                        'Provide additional resources if helpful',
                        'Encourage continued engagement'
                      ] : openStepPopup === 4 ? [
                        'Thank them for participating',
                        'Ask for honest feedback about their experience',
                        'Include specific questions about the program',
                        'Make it easy to respond'
                      ] : openStepPopup === 5 ? [
                        'Welcome participants to the program',
                        'Explain the program goals and timeline',
                        'Share onboarding materials and guidelines',
                        'Set expectations for participation',
                        'Provide contact information for support'
                      ] : openStepPopup === 6 ? [
                        'Share tips for meaningful conversations',
                        'Suggest meeting formats and frequencies',
                        'Provide icebreaker questions',
                        'Encourage active listening and openness'
                      ] : openStepPopup === 7 ? [
                        'Check satisfaction with the program so far',
                        'Ask about connection quality',
                        'Gather suggestions for improvement',
                        'Keep it brief and easy to complete'
                      ] : openStepPopup === 8 ? [
                        'Congratulate participants on completing the program',
                        'Highlight key achievements and milestones',
                        'Encourage continued connection',
                        'Invite them to future programs'
                      ] : openStepPopup === 9 ? [
                        'Share relevant articles or guides',
                        'Provide conversation topic suggestions',
                        'Include helpful tools or templates',
                        'Offer additional support resources'
                      ] : openStepPopup === 10 ? [
                        'Express gratitude for their participation',
                        'Summarize program outcomes',
                        'Invite them to stay connected',
                        'Provide information about upcoming programs'
                      ] : openStepPopup === 11 ? [
                        'Confirm both participants have opted in to connect',
                        'Introduce each participant to the other with key highlights',
                        'Explain why they were matched and what they have in common',
                        'Provide suggested next steps for scheduling a meeting',
                        'Include conversation starters tailored to their shared interests'
                      ] : [
                        'Add your custom key points here'
                      ]).map((point, index) => (
                        <div key={index} className="flex items-start gap-2 bg-white dark:bg-navy-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 group">
                          <div className="text-brand-400 dark:text-brand-500 mt-1">
                            <MdCircle className="h-2 w-2" />
                          </div>
                          <input
                            type="text"
                            defaultValue={point}
                            className="flex-1 bg-transparent text-sm text-navy-700 dark:text-white focus:outline-none"
                            placeholder="Add a key point..."
                          />
                          <button
                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
                            onClick={() => {
                              // TODO: Remove key point
                            }}
                          >
                            <MdClose className="h-4 w-4" />
                          </button>
                        </div>
                      ))}

                      {/* Add new key point */}
                      <button
                        onClick={() => {
                          // TODO: Add new key point
                        }}
                        className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm text-gray-500 dark:text-gray-400 hover:border-brand-500 hover:text-brand-600 dark:hover:text-brand-400 transition"
                      >
                        <MdAdd className="h-4 w-4" />
                        Add Key Point
                      </button>
                    </div>
                  </div>

                  {/* Tone and Style (Optional) */}
                  <div>
                    <label className="block text-sm font-medium text-navy-700 dark:text-white mb-2">
                      Tone & Style (Optional)
                    </label>
                    <select className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-navy-900 text-navy-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500">
                      <option>Friendly and warm</option>
                      <option>Professional and formal</option>
                      <option>Casual and conversational</option>
                      <option>Encouraging and supportive</option>
                    </select>
                  </div>

                  {/* Additional Instructions */}
                  <div>
                    <label className="block text-sm font-medium text-navy-700 dark:text-white mb-2">
                      Additional Instructions (Optional)
                    </label>
                    <textarea
                      rows={3}
                      placeholder="Any other specific instructions for the AI when generating this email..."
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-navy-900 text-navy-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => {
                        // TODO: Save customization
                        setOpenStepPopup(null);
                      }}
                      className="flex-1 px-4 py-2 rounded-lg bg-brand-500 text-white font-medium hover:bg-brand-600 transition"
                    >
                      Save Guidelines
                    </button>
                    <button
                      onClick={() => setOpenStepPopup(null)}
                      className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-navy-700 transition"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Cohort Modal */}
      {showCreateCohortModal && (
        <div
          className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => setShowCreateCohortModal(false)}
        >
          <div
            className="modal-content bg-white dark:bg-navy-800 rounded-xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                Create New Cohort
              </h3>
              <button
                onClick={() => setShowCreateCohortModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <MdClose className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-navy-700 dark:text-white mb-2">
                  Cohort Name
                </label>
                <input
                  type="text"
                  value={cohortName}
                  onChange={(e) => setCohortName(e.target.value)}
                  placeholder="Enter cohort name..."
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-navy-900 text-navy-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-xs text-blue-700 dark:text-blue-400">
                  <strong>Workflow:</strong> {workflowPresets.find(p => p.id === selectedPreset)?.name}
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-500 mt-1">
                  {actions.filter(a => a.enabled).length} actions will be configured for this cohort.
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowCreateCohortModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-navy-700 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateCohort}
                  disabled={isCreatingCohort || !cohortName.trim()}
                  className="flex-1 px-4 py-2 rounded-lg bg-brand-500 text-white font-medium hover:bg-brand-600 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isCreatingCohort ? 'Creating...' : 'Create Cohort'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntroductionTemplates;
